import { useState } from "react";
import {
  ClipboardList, ChevronDown, ChevronUp, X, AlertTriangle, MessageSquare,
  Check, ArrowRight, FileSpreadsheet,
} from "lucide-react";
import { Button } from "@/components/ui/button";

/**
 * Import Summary — shown after every structured takeoff upload (FIX-7).
 * Pure presentation: counts, flagged lines, clarifications, AI-match reasons.
 * Actions bubble up to the Builder.
 */
function Stat({ label, value, tone = "slate", testid }) {
  const tones = {
    slate: "text-slate-800",
    emerald: "text-emerald-700",
    amber: "text-amber-700",
    rose: "text-rose-700",
  };
  return (
    <div className="px-3 py-2 bg-white border border-slate-200 rounded-sm min-w-[86px]" data-testid={testid}>
      <div className={`text-lg font-semibold leading-none ${tones[tone]}`} style={{ fontFamily: "Cabinet Grotesk, sans-serif" }}>
        {value}
      </div>
      <div className="text-[10px] text-slate-500 mt-1">{label}</div>
    </div>
  );
}

export default function ImportSummaryPanel({
  summaries = [], clarifications = [], flags = [],
  onDismiss, onJumpToFlag, onMoveFlag, onResolveClarification, onRemoveClarification,
}) {
  const [open, setOpen] = useState(true);
  if (!summaries.length && !clarifications.length) return null;

  const total = summaries.reduce((a, s) => ({
    rows_read: a.rows_read + (s.rows_read || 0),
    rows_imported: a.rows_imported + (s.rows_imported || 0),
    rows_summed: a.rows_summed + (s.rows_summed || 0),
    lines_created: a.lines_created + (s.lines_created || 0),
    flagged: a.flagged + (s.flagged || 0),
    annotations_ignored: a.annotations_ignored + (s.annotations_ignored || 0),
    clarifications_captured: a.clarifications_captured + (s.clarifications_captured || 0),
    ai: a.ai + ((s.ai_matched || []).length),
  }), { rows_read: 0, rows_imported: 0, rows_summed: 0, lines_created: 0, flagged: 0, annotations_ignored: 0, clarifications_captured: 0, ai: 0 });

  const openClarifications = clarifications.filter(c => !c.resolved);
  // The flagged tile tracks what is STILL flagged in the budget — moving or
  // fixing a flag drops the count. `total.flagged` is the number recorded at
  // import time and is shown beside it when they differ.
  const liveFlagged = flags.length;

  return (
    <div className="px-4 py-3 bg-[#161616]/[0.03] border-b border-slate-200" data-testid="import-summary-panel">
      <div className="flex items-start justify-between gap-3">
        <div className="flex items-start gap-2 min-w-0 flex-1">
          <div className="mt-0.5 w-6 h-6 rounded-full bg-[#303E41] text-white flex items-center justify-center shrink-0">
            <ClipboardList className="w-3.5 h-3.5" />
          </div>
          <div className="min-w-0 flex-1">
            <div className="text-xs font-semibold text-[#161616]">
              Takeoff import — {summaries.length === 1 ? "1 file" : `${summaries.length} files`} read row by row
            </div>
            <div className="text-[11px] text-slate-500 mt-0.5 truncate" data-testid="import-summary-files">
              {summaries.map(s => s.file).join(" · ")}
            </div>
          </div>
        </div>
        <div className="flex items-center gap-1 shrink-0">
          <Button
            variant="ghost" size="sm" onClick={() => setOpen(o => !o)}
            className="h-7 px-2 text-xs text-slate-600 hover:bg-slate-100 rounded-sm"
            data-testid="import-summary-toggle"
          >
            {open ? <ChevronUp className="w-3.5 h-3.5" /> : <ChevronDown className="w-3.5 h-3.5" />}
            {open ? "Hide" : "Show summary"}
          </Button>
          {onDismiss && (
            <button onClick={onDismiss} className="p-1 text-slate-400 hover:text-slate-700 rounded-sm"
              aria-label="Dismiss import summary" data-testid="import-summary-dismiss">
              <X className="w-4 h-4" />
            </button>
          )}
        </div>
      </div>

      {open && (
        <div className="mt-3 ml-8 space-y-3">
          <div className="flex flex-wrap gap-2">
            <Stat label="rows read" value={total.rows_read} testid="stat-rows-read" />
            <Stat label="imported" value={total.rows_imported} tone="emerald" testid="stat-imported" />
            <Stat label="summed" value={total.rows_summed} testid="stat-summed" />
            <Stat label="lines" value={total.lines_created} testid="stat-lines" />
            <Stat label="flagged" value={liveFlagged} tone={liveFlagged ? "rose" : "emerald"} testid="stat-flagged" />
            {total.flagged !== liveFlagged && (
              <div className="px-3 py-2 bg-white border border-slate-200 rounded-sm text-[10px] text-slate-500 flex items-center" data-testid="stat-flagged-resolved">
                {total.flagged - liveFlagged} flag{total.flagged - liveFlagged === 1 ? "" : "s"}<br />resolved since import
              </div>
            )}
            <Stat label="annotations ignored" value={total.annotations_ignored} testid="stat-ignored" />
            <Stat label="clarifications" value={total.clarifications_captured} tone="amber" testid="stat-clarifications" />
            <Stat label="AI-matched" value={total.ai} testid="stat-ai" />
          </div>

          {summaries.map((s, si) => (
            <div key={si} className="space-y-2" data-testid={`import-summary-file-${si}`}>
              {!!(s.warnings || []).length && (
                <ul className="space-y-1">
                  {s.warnings.map((w, i) => (
                    <li key={i} className="flex items-start gap-2 text-xs text-rose-700" data-testid={`import-warning-${si}-${i}`}>
                      <AlertTriangle className="w-3.5 h-3.5 shrink-0 mt-0.5" />
                      <span>{w}</span>
                    </li>
                  ))}
                </ul>
              )}
              {!!(s.unknown_subjects || []).length && (
                <div className="text-xs text-amber-700" data-testid={`import-unknown-${si}`}>
                  Unmapped subjects:{" "}
                  {s.unknown_subjects.map(u => `${u.subject} (${u.rows} row${u.rows === 1 ? "" : "s"}, qty ${u.quantity})`).join(", ")}
                  {" — add these scopes to the budget manually."}
                </div>
              )}
              {!!(s.annotation_breakdown && Object.keys(s.annotation_breakdown).length) && (
                <div className="text-[11px] text-slate-500" data-testid={`import-annotations-${si}`}>
                  Ignored:{" "}
                  {Object.entries(s.annotation_breakdown).map(([k, v]) => `${k} ×${v}`).join(", ")}
                </div>
              )}
              {!!(s.ai_matched || []).length && (
                <details className="text-xs" data-testid={`import-ai-list-${si}`}>
                  <summary className="cursor-pointer text-slate-600 hover:text-[#161616]">
                    {s.ai_matched.length} line{s.ai_matched.length === 1 ? "" : "s"} chosen by the AI matcher — show reasoning
                  </summary>
                  <ul className="mt-1.5 space-y-1 pl-3 border-l border-slate-200">
                    {s.ai_matched.map((a, i) => (
                      <li key={i} className="text-slate-600">
                        <span className="font-medium text-slate-800">{a.label}</span>
                        <ArrowRight className="inline w-3 h-3 mx-1 text-slate-400" />
                        <span className="text-slate-800">{a.template}</span>
                        <span className="text-slate-400"> · {a.scope}</span>
                        <div className="text-[11px] text-slate-500">{a.reason}</div>
                      </li>
                    ))}
                  </ul>
                </details>
              )}
            </div>
          ))}

          {!!flags.length && (
            <div className="bg-white border border-rose-200 rounded-sm" data-testid="import-flags">
              <div className="px-3 py-2 border-b border-rose-100 text-xs font-semibold text-rose-700 flex items-center gap-1.5">
                <AlertTriangle className="w-3.5 h-3.5" />
                {flags.length} flagged line{flags.length === 1 ? "" : "s"} — nothing was dropped, nothing was guessed
              </div>
              <ul className="divide-y divide-rose-50">
                {flags.map((f, i) => (
                  <li key={f.item_id} className="px-3 py-2 flex items-start justify-between gap-3" data-testid={`import-flag-${i}`}>
                    <div className="min-w-0">
                      <button
                        onClick={() => onJumpToFlag?.(f)}
                        className="text-xs font-medium text-slate-800 hover:text-[#161616] hover:underline text-left"
                        data-testid={`import-flag-jump-${i}`}
                      >
                        {f.description}
                        <span className="text-slate-400 font-normal"> · qty {f.qty} · {f.scope_name}</span>
                      </button>
                      <div className="text-[11px] text-slate-500">{f.flag?.message}</div>
                      {!!(f.source_rows || []).length && (
                        <div className="text-[10px] text-slate-400 mt-0.5 flex items-center gap-1">
                          <FileSpreadsheet className="w-3 h-3" />
                          {f.source_rows.slice(0, 4).join(" · ")}{f.source_rows.length > 4 ? ` +${f.source_rows.length - 4}` : ""}
                        </div>
                      )}
                    </div>
                    {f.flag?.suggested_scope_code && (
                      <Button
                        size="sm" variant="outline"
                        onClick={() => onMoveFlag?.(f)}
                        className="shrink-0 h-7 rounded-sm border-slate-300 text-xs gap-1"
                        data-testid={`import-flag-move-${i}`}
                      >
                        Move to {f.flag.suggested_scope_name || f.flag.suggested_scope_code}
                      </Button>
                    )}
                  </li>
                ))}
              </ul>
            </div>
          )}

          {!!clarifications.length && (
            <div className="bg-white border border-amber-200 rounded-sm" data-testid="import-clarifications">
              <div className="px-3 py-2 border-b border-amber-100 text-xs font-semibold text-amber-700 flex items-center gap-1.5">
                <MessageSquare className="w-3.5 h-3.5" />
                Clarifications from the takeoff callouts ({openClarifications.length} open)
              </div>
              <ul className="divide-y divide-amber-50">
                {clarifications.map((c, i) => (
                  <li key={c.id || i} className="px-3 py-2 flex items-start gap-2" data-testid={`clarification-${i}`}>
                    <button
                      type="button" role="checkbox" aria-checked={!!c.resolved}
                      onClick={() => onResolveClarification?.(c)}
                      className={`shrink-0 mt-0.5 w-4 h-4 rounded-sm border flex items-center justify-center ${
                        c.resolved ? "bg-emerald-500 border-emerald-500 text-white" : "bg-white border-slate-300 hover:border-emerald-400"
                      }`}
                      title={c.resolved ? "Mark as open" : "Mark as resolved"}
                      data-testid={`clarification-${i}-check`}
                    >
                      {c.resolved && <Check className="w-3 h-3" strokeWidth={3} />}
                    </button>
                    <div className="min-w-0 flex-1">
                      <div className={`text-xs ${c.resolved ? "line-through text-slate-400" : "text-slate-700"}`}>
                        {c.text}
                      </div>
                      <div className="text-[10px] text-slate-400">{c.page_label}{c.source_file ? ` · ${c.source_file}` : ""}</div>
                    </div>
                    {onRemoveClarification && (
                      <button
                        onClick={() => onRemoveClarification(c)}
                        className="p-1 text-slate-300 hover:text-rose-500"
                        title="Remove clarification"
                        data-testid={`clarification-${i}-remove`}
                      >
                        <X className="w-3.5 h-3.5" />
                      </button>
                    )}
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
